import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import {
  assertCanUpdateObligationStatus,
  getAuthUser,
  sanitizeObligationFormData,
  sanitizeObligationSavePayload,
  sanitizeProposalResponsePayload,
} from '@/shared/auth/obligationAuth';

/**
 * ObligationService - Servis za upravljanje obavezama
 * 
 * Omogućava komunikaciju sa Obligation API endpoint-ima:
 * - POST /obligation/search - Pretraga obaveza
 * - POST /obligation/save-or-update - Dodavanje/editovanje obaveze
 * - POST /obligation/status/update - Promena statusa obaveze
 * - POST /obligation/related-organization/search - Pretraga povezanih organizacija
 * - POST /obligation/related-organization/add - Dodavanje povezane organizacije
 * - POST /obligation/related-organization/remove - Uklanjanje povezane organizacije
 * - POST /obligation/moderator/add - Dodavanje moderatora
 * - POST /obligation/moderator/remove - Uklanjanje moderatora
 * - POST /obligation/moderator/accept-moderation - Prihvatanje moderacije
 * - POST /obligation/moderator/reject-moderation - Odbijanje moderacije
 */
class ObligationService {
    getPhaseStatusTarget(phase) {
        const map = {
            PROPOSAL: 'CREATED',
            ALIGNMENT: 'UNDER_REVIEW',
            PLANNING: 'ACCEPTED',
        };
        return map[phase] || 'CREATED';
    }

    /**
     * Pretražuje obaveze (POST /obligation/search).
     * Filteri se mogu kombinovati (npr. action_plan_id + proposed_by + proposed_by_organization_id) — backend ih tretira kao uslove pretrage.
     * @param {Object} filters
     * @param {string} [filters.action_plan_id]
     * @param {string|null} [filters.proposed_by_organization_id] — ako je eksplicitno prosleđeno (uklj. `null`), ide u body (FL pretraga: `null` kao „bez organizacije”).
     * @param {string} [filters.proposed_by] — UUID naloga; email se ne šalje (backend očekuje UUID)
     * @param {string} [filters.id] — ID obaveze
     * @returns {Promise<{ obligations: Array }>}
     */
    async search(filters = {}, signal = null) {
        try {
            const requestBody = {};

            if (filters.action_plan_id) {
                requestBody.action_plan_id = filters.action_plan_id;
            }
            if (Object.prototype.hasOwnProperty.call(filters, 'proposed_by_organization_id')) {
                requestBody.proposed_by_organization_id = filters.proposed_by_organization_id;
            }
            if (
                filters.proposed_by != null &&
                String(filters.proposed_by).trim() !== '' &&
                !String(filters.proposed_by).includes('@')
            ) {
                requestBody.proposed_by = String(filters.proposed_by).trim();
            }

            const hasActionPlan = !!requestBody.action_plan_id;
            const hasProposedBy = !!requestBody.proposed_by;
            const orgRaw = requestBody.proposed_by_organization_id;
            const hasMeaningfulOrg =
                Object.prototype.hasOwnProperty.call(requestBody, 'proposed_by_organization_id') &&
                orgRaw != null &&
                String(orgRaw).trim() !== '';

            if (!hasActionPlan && !hasProposedBy && !hasMeaningfulOrg) {
                throw new Error('Za pretragu obaveza potreban je action_plan_id, proposed_by_organization_id ili proposed_by');
            }

            if (filters.id) {
                requestBody.id = filters.id;
            }

            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.SEARCH, requestBody, {}, signal);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Čuva ili ažurira obavezu
     * @param {Object} obligation - Obaveza koja se čuva/ažurira
     * @param {string} obligation.id - ID obaveze (opciono za novu obavezu)
     * @param {string} obligation.action_plan_id - ID akcionog plana
     * @param {string} obligation.name - Naziv obaveze
     * @param {string} obligation.number - Broj obaveze
     * @param {string} obligation.description - Opis obaveze
     * @param {string} obligation.created - Datum kreiranja (ISO format)
     * @param {string} obligation.proposed_by - ID korisnika koji je predložio
     * @param {string} obligation.proposed_by_organization_id - ID organizacije koja je predložila
     * @param {string} obligation.planned_obligation_start - Planirani početak (ISO format)
     * @param {string} obligation.planned_obligation_end - Planirani završetak (ISO format)
     * @returns {Promise<Object>} Sačuvana obaveza
     */
    /**
     * Uklanja HTML tagove iz teksta
     * @private
     * @param {string} text - Tekst koji može sadržati HTML tagove
     * @returns {string} Čist tekst bez HTML tagova
     */
    _stripHtml(text) {
        if (!text || typeof text !== 'string') return '';
        return text.replace(/<[^>]*>/g, '').trim();
    }

    async saveOrUpdate(obligation) {
        try {
            const user = await getAuthUser();
            // Uklanjamo HTML tagove iz description polja pre slanja
            const cleanedObligation = sanitizeObligationSavePayload(
                {
                    ...obligation,
                    description: obligation.description ? this._stripHtml(obligation.description) : undefined,
                },
                { user },
            );
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.SAVE_OR_UPDATE,
                cleanedObligation,
            );
            // Proveravamo da li je status 200
            if (response.status === 200 || (response.status >= 200 && response.status < 300)) {
                return response.data;
            }
            throw new Error(`Unexpected status code: ${response.status}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Čuva ili ažурira обавезу са прилогом (FormData – document).
     * @param {FormData} formData - FormData са пољима обавезе и 'document' фајлом
     * @returns {Promise<Object>} Sačuvana obaveza
     */
    async saveOrUpdateWithFormData(formData) {
        try {
            const user = await getAuthUser();
            // Za FormData, description se već postavlja kao čist tekst u useObligationsTabLogic.js
            // Ali proveravamo da li postoji description i uklanjamo HTML tagove ako postoje
            if (formData instanceof FormData && formData.has('description')) {
                const descriptionValue = formData.get('description');
                if (descriptionValue && typeof descriptionValue === 'string') {
                    formData.set('description', this._stripHtml(descriptionValue));
                }
            }
            sanitizeObligationFormData(formData, { user });
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.SAVE_OR_UPDATE,
                formData,
            );
            // Proveravamo da li je status 200
            if (response.status === 200 || (response.status >= 200 && response.status < 300)) {
                return response.data;
            }
            throw new Error(`Unexpected status code: ${response.status}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Menja status obaveze
     * @param {string} obligationId - ID obaveze
     * @param {string} status - Novi status (npr. "CREATED", "DRAFT", "IMPLEMENTATION", "COMPLETED")
     * @returns {Promise<Object>} Odgovor servera
     */
    async updateStatus(obligationId, status) {
        try {
            const user = await getAuthUser();
            assertCanUpdateObligationStatus(user);
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.STATUS_UPDATE, {
                obligation_id: obligationId,
                status: status
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async submitPhase(obligationId, phase) {
        const status = this.getPhaseStatusTarget(phase);
        return this.updateStatus(obligationId, status);
    }

    async reopenPhase(obligationId, phase) {
        const status = this.getPhaseStatusTarget(phase);
        return this.updateStatus(obligationId, status);
    }

    /**
     * Menja status predloga obaveze (obligation proposal)
     * @param {string} obligationProposalId - ID predloga obaveze
     * @param {string} status - Novi status (npr. "CREATED", "UNDER_REVIEW", "ACCEPTED", "REJECTED")
     * @returns {Promise<Object>} Odgovor servera
     */
    async updateProposalStatus(obligationProposalId, status) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.PROPOSAL_STATUS_UPDATE, {
                obligation_proposal_id: obligationProposalId,
                status: status
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Čuva ili ažurira odgovor na pitanje predloga obaveze (obligation proposal response)
     * @param {Object} proposalResponse - Odgovor na pitanje predloga obaveze
     * @param {string} proposalResponse.obligation_id - ID obaveze
     * @param {number} proposalResponse.obligation_question_id - ID pitanja (1-7)
     * @param {string} proposalResponse.response - Odgovor na pitanje
     * @param {string} proposalResponse.proposer_id - ID predlagaca (opciono)
     * @param {string} proposalResponse.creator_participant_type - Tip učesnika (PROPOSER, MODERATOR, ADMIN)
     * @returns {Promise<Object>} Sačuvan odgovor
     */
    async saveProposalResponse(proposalResponse, options = {}) {
        try {
            const user = await getAuthUser();
            // Uklanjamo HTML tagove iz response polja pre slanja
            const cleanedResponse = sanitizeProposalResponsePayload(
                {
                    ...proposalResponse,
                    response: this._stripHtml(proposalResponse.response || ''),
                },
                {
                    user,
                    isAdmin: options?.isAdmin,
                    consensusPersist: options?.consensusPersist,
                },
            );
            // options.headers omogućava caller override (npr. consensus actor šalje eksplicitni X-Role)
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.PROPOSAL_SAVE_OR_UPDATE,
                cleanedResponse,
                options?.headers || {},
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje odgovore na pitanja predloga obaveze (obligation proposal responses)
     * @param {Object} filters - Filteri za pretragu
     * @param {string} filters.obligation_id - ID obaveze
     * @returns {Promise<Object>} Objekat sa obligation_proposals nizom
     */
    async searchProposalResponses(filters = {}) {
        try {
            const requestBody = {};
            if (filters.obligation_id) {
                requestBody.obligation_id = filters.obligation_id;
            }
            if (!requestBody.obligation_id) {
                throw new Error('Za pretragu odговора на питања предлога обавезе potreban je obligation_id');
            }
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.PROPOSAL_SEARCH, requestBody);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Vraća obligation_id za dati proposal_id (POST /obligation/proposal/search-one).
     * @param {string} proposalId
     * @returns {Promise<{ proposal_id: string, obligation_id: string }|null>}
     */
    async searchOneProposal(proposalId) {
        if (!proposalId) {
            throw new Error('Za pretragu predloga obaveze potreban je proposal_id');
        }
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.PROPOSAL_SEARCH_ONE, {
                proposal_id: proposalId,
            });
            const data = response?.data?.data ?? response?.data ?? response ?? null;
            const obligationId =
                data?.obligation_id ??
                data?.obligationId ??
                data?.obligation?.id ??
                null;
            if (!obligationId) return null;
            return {
                proposal_id: String(data?.proposal_id ?? data?.proposalId ?? data?.id ?? proposalId),
                obligation_id: String(obligationId),
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje arhiv odgovora na pitanja predloga obaveze (obligation proposal archive responses)
     * Vraća istoriju za sva polja sortiranu po datumu
     * @param {Object} filters - Filteri za pretragu
     * @param {string} filters.obligation_id - ID obaveze
     * @returns {Promise<Object>} Objekat sa obligation_proposals nizom (istorija za sva polja)
     */
    async searchProposalArchiveResponses(filters = {}) {
        try {
            const requestBody = {};
            if (filters.obligation_id) {
                requestBody.obligation_id = filters.obligation_id;
            }
            if (!requestBody.obligation_id) {
                throw new Error('Za pretragu arhive odговора на питања предлога обавезе potreban je obligation_id');
            }
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.PROPOSAL_ARCHIVE_SEARCH, requestBody);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje povezane organizacije za obavezu
     * @param {string} obligationId - ID obaveze
     * @returns {Promise<Object>} Objekat sa organizations nizom
     */
    async searchRelatedOrganizations(obligationId) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.RELATED_ORGANIZATION_SEARCH, {
                obligation_id: obligationId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Dodaje povezanu organizaciju obavezi
     * @param {string} obligationId - ID obaveze
     * @param {string} organizationId - ID organizacije
     * @param {boolean} mainResponsible - Da li je organizacija glavna odgovorna
     * @returns {Promise<Object>} Odgovor servera
     */
    async addRelatedOrganization(obligationId, organizationId, mainResponsible = false) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.RELATED_ORGANIZATION_ADD, {
                obligation_id: obligationId,
                organization_id: organizationId,
                main_responsible: mainResponsible
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Uklanja povezanu organizaciju iz obaveze
     * @param {string} obligationId - ID obaveze
     * @param {string} organizationId - ID organizacije
     * @returns {Promise<Object>} Odgovor servera
     */
    async removeRelatedOrganization(obligationId, organizationId) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.RELATED_ORGANIZATION_REMOVE, {
                obligation_id: obligationId,
                organization_id: organizationId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Dodaje moderatora obavezi
     * @param {string} obligationId - ID obaveze
     * @param {string} moderatorAccountId - ID naloga moderatora
     * @returns {Promise<Object>} Odgovor servera
     */
    async addModerator(obligationId, moderatorAccountId) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.MODERATOR_ADD, {
                moderator_account_id: moderatorAccountId,
                obligation_id: obligationId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Uklanja moderatora iz obaveze
     * @param {string} obligationId - ID obaveze
     * @param {string} moderatorAccountId - ID naloga moderatora
     * @returns {Promise<Object>} Odgovor servera
     */
    async removeModerator(obligationId, moderatorAccountId) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.OBLIGATION.MODERATOR_REMOVE, {
                moderator_account_id: moderatorAccountId,
                obligation_id: obligationId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Moderator prihvata zahtev za moderaciju obaveze.
     * X-Role: MODERATOR (funkcionalni moderator dozvoljen) — rešava endpointRolePolicy.
     * @param {string} obligationId
     * @returns {Promise<Object>}
     */
    async acceptModeration(obligationId) {
        try {
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.MODERATOR_ACCEPT,
                { obligation_id: obligationId },
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Moderator odbija zahtev za moderaciju obaveze.
     * X-Role: MODERATOR (funkcionalni moderator dozvoljen) — rešava endpointRolePolicy.
     * @param {string} obligationId
     * @returns {Promise<Object>}
     */
    async rejectModeration(obligationId) {
        try {
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.MODERATOR_REJECT,
                { obligation_id: obligationId },
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Čuva ili ažurira nacrt obaveze (POST /obligation/draft/save-or-update).
     * @param {Object} draft
     * @returns {Promise<Object>}
     */
    async saveOrUpdateDraft(draft) {
        try {
            const user = await getAuthUser();
            const cleaned = sanitizeObligationSavePayload(
                {
                    ...draft,
                    description: draft.description ? this._stripHtml(draft.description) : draft.description,
                },
                { user },
            );
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.DRAFT_SAVE_OR_UPDATE,
                cleaned,
            );
            if (response.status === 200 || (response.status >= 200 && response.status < 300)) {
                return response.data;
            }
            throw new Error(`Unexpected status code: ${response.status}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretraga nacrta obaveza za ulogovanog korisnika (POST /obligation/draft/search).
     * @param {Object} [filters]
     * @param {string} [filters.action_plan_id]
     * @param {string} [filters.id]
     * @returns {Promise<{ obligations: Array }>}
     */
    async searchDrafts(filters = {}, signal = null) {
        try {
            const requestBody = {};
            if (filters.action_plan_id) {
                requestBody.action_plan_id = filters.action_plan_id;
            }
            if (filters.id) {
                requestBody.id = filters.id;
            }
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.DRAFT_SEARCH,
                requestBody,
                {},
                signal,
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Šalje nacrt obaveze u status CREATED (POST /obligation/draft/submit).
     * @param {string} obligationId
     * @returns {Promise<Object>}
     */
    async submitDraft(obligationId) {
        try {
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.OBLIGATION.DRAFT_SUBMIT,
                { obligation_id: obligationId },
            );
            if (response.status === 200 || (response.status >= 200 && response.status < 300)) {
                return response.data;
            }
            throw new Error(`Unexpected status code: ${response.status}`);
        } catch (error) {
            throw error;
        }
    }
}

const obligationServiceInstance = new ObligationService();
export default obligationServiceInstance;
